"use client"

import { FormSection } from "@/(admin)/components/form"
import {
  API_PREFIX_BANNER_PATH,
  API_PREFIX_CAREER_PATH,
  API_PREFIX_EVENT_PATH,
  API_PREFIX_LETAKY_PATH,
  API_PREFIX_POST_PATH,
  API_PREFIX_ROLES_PATH,
} from "@/constants/prefix-path-api"
import { Button, Form, FormInstance, Input, Select, Spin } from "antd"
import { t } from "i18next"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { domainOptions } from "@/(admin)/modules/page/pages/List"
import { HttpClient } from "../../../../app/api/HttpClient"
import {
  fetchWebsiteRelationPage,
  WEBSITE_RELATION_PAGE_SIZE,
  type WebsiteRelation,
  type WebsiteRelationBaseline,
} from "../services/website"

type SelectOption = { value: number; label: string }

export interface WebsiteFieldType {
  name: string
  domain: string
  letak?: number | null
  career?: number | null
  posts?: number[]
  banners?: number[]
  events?: number[]
  roles?: number[]
}

export interface WebsiteFormProps {
  form: FormInstance<WebsiteFieldType>
  onFinish: (values: WebsiteFieldType) => void
  onFinishFailed: (errorInfo: any) => void
  onValuesChange?: () => void
  edit?: boolean
  websiteData?: any
  /** Edit mode only — drives the paginated load of posts/banners/events. */
  websiteId?: number
  /** Filled with the relation ids this form loaded; the page turns it into a connect/disconnect delta. */
  relationBaselineRef?: React.MutableRefObject<WebsiteRelationBaseline>
  /** Fired once all three relations have their first page in. */
  onRelationsReady?: () => void
}

const getLabel = (attrs: any): string =>
  attrs?.title ?? attrs?.name ?? ""

const toOptions = (data: any[]): SelectOption[] =>
  (data || [])
    .filter(i => i?.id)
    .map(i => ({
      value: i.id,
      label: getLabel(i.attributes) || `ID: ${i.id}`,
    }))

const mergeOptions = (existing: SelectOption[], incoming: SelectOption[]): SelectOption[] => {
  const map = new Map(existing.map(o => [o.value, o]))
  incoming.forEach(o => map.set(o.value, o))
  return Array.from(map.values())
}

interface AssignedRelation {
  options: SelectOption[]
  addOptions: (incoming: SelectOption[]) => void
  /** assigned rows paged in so far (excludes search results) */
  loadedCount: number
  total: number
  loading: boolean
  loadingMore: boolean
  loadMore: () => void
}

/**
 * Pages the rows already linked to this website in from their own collection,
 * 20 at a time. The website record itself no longer populates these relations
 * (Strapi v4 has no pagination for populated relations — see website service),
 * so this is the only place the current selection is loaded.
 *
 * The ids loaded so far are mirrored into `baselineRef` so the page can diff
 * them against the field value and save a connect/disconnect delta, never a
 * full replace — rows past the last loaded page must not be unlinked.
 */
const useAssignedRelation = (
  relation: WebsiteRelation,
  websiteId: number | undefined,
  form: FormInstance<WebsiteFieldType>,
  baselineRef: React.MutableRefObject<WebsiteRelationBaseline> | undefined,
  onLoaded: (() => void) | undefined,
): AssignedRelation => {
  const [options, setOptions] = useState<SelectOption[]>([])
  const [loadedCount, setLoadedCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const loadedRef = useRef<SelectOption[]>([])
  const pageRef = useRef(0)
  const onLoadedRef = useRef(onLoaded)
  onLoadedRef.current = onLoaded

  const applyPage = useCallback(
    (incoming: SelectOption[], replace: boolean) => {
      const merged = replace ? incoming : mergeOptions(loadedRef.current, incoming)
      loadedRef.current = merged
      setOptions(prev => mergeOptions(prev, merged))
      setLoadedCount(merged.length)

      const ids = merged.map(o => o.value)
      form.setFieldValue(relation, ids)
      if (baselineRef) baselineRef.current[relation] = ids
    },
    [form, relation, baselineRef],
  )

  useEffect(() => {
    if (!websiteId) return
    let cancelled = false

    setLoading(true)
    fetchWebsiteRelationPage(websiteId, relation, 1)
      .then(({ data, total: nextTotal }) => {
        if (cancelled) return
        pageRef.current = 1
        setTotal(nextTotal)
        applyPage(toOptions(data), true)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
        onLoadedRef.current?.()
      })

    return () => {
      cancelled = true
    }
  }, [websiteId, relation, applyPage])

  const loadMore = useCallback(async () => {
    if (!websiteId || loadingMore) return
    setLoadingMore(true)
    try {
      const nextPage = pageRef.current + 1
      const { data, total: nextTotal } = await fetchWebsiteRelationPage(websiteId, relation, nextPage)
      pageRef.current = nextPage
      setTotal(nextTotal)
      applyPage(toOptions(data), false)
    } finally {
      setLoadingMore(false)
    }
  }, [websiteId, relation, loadingMore, applyPage])

  const addOptions = useCallback((incoming: SelectOption[]) => {
    setOptions(prev => mergeOptions(prev, incoming))
  }, [])

  return { options, addOptions, loadedCount, total, loading, loadingMore, loadMore }
}

const WebsiteForm: React.FC<WebsiteFormProps> = ({
  form,
  onFinish,
  onFinishFailed,
  onValuesChange,
  websiteData,
  websiteId,
  relationBaselineRef,
  onRelationsReady,
}) => {
  const [letakOptions, setLetakOptions] = useState<SelectOption[]>([])
  const [careerOptions, setCareerOptions] = useState<SelectOption[]>([])
  const [roleOptions, setRoleOptions] = useState<SelectOption[]>([])

  const [letakLoading, setLetakLoading] = useState(false)
  const [careerLoading, setCareerLoading] = useState(false)
  const [postSearching, setPostSearching] = useState(false)
  const [bannerSearching, setBannerSearching] = useState(false)
  const [eventSearching, setEventSearching] = useState(false)
  const [roleLoading, setRoleLoading] = useState(false)

  // One debounce timer per endpoint — a shared timer let a focus on one select
  // cancel another select's in-flight option load.
  const searchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  // Only fire onRelationsReady once all three first pages have settled.
  const pendingRelationsRef = useRef(3)
  const handleRelationLoaded = useCallback(() => {
    pendingRelationsRef.current -= 1
    if (pendingRelationsRef.current === 0) onRelationsReady?.()
  }, [onRelationsReady])

  const posts = useAssignedRelation("posts", websiteId, form, relationBaselineRef, handleRelationLoaded)
  const banners = useAssignedRelation("banners", websiteId, form, relationBaselineRef, handleRelationLoaded)
  const events = useAssignedRelation("events", websiteId, form, relationBaselineRef, handleRelationLoaded)

  // Initialize the small relations from the website record (edit mode).
  useEffect(() => {
    if (!websiteData?.attributes) return
    const attrs = websiteData.attributes

    if (attrs.letak?.data?.id) {
      const opt: SelectOption = {
        value: attrs.letak.data.id,
        label: getLabel(attrs.letak.data.attributes) || `ID: ${attrs.letak.data.id}`,
      }
      setLetakOptions([opt])
      form.setFieldValue("letak", attrs.letak.data.id)
    }

    if (attrs.career?.data?.id) {
      const opt: SelectOption = {
        value: attrs.career.data.id,
        label: getLabel(attrs.career.data.attributes) || `ID: ${attrs.career.data.id}`,
      }
      setCareerOptions([opt])
      form.setFieldValue("career", attrs.career.data.id)
    }

    if (attrs.roles?.data?.length) {
      const opts = toOptions(attrs.roles.data)
      setRoleOptions(prev => mergeOptions(prev, opts))
      form.setFieldValue("roles", opts.map(o => o.value))
    }
  }, [websiteData, form])

  // Load all roles once on mount (small fixed list)
  useEffect(() => {
    const load = async () => {
      setRoleLoading(true)
      try {
        const response = await HttpClient.get(API_PREFIX_ROLES_PATH)
        // users-permissions returns { roles: [...] }, not { data: [...] }
        const raw = response?.roles || response?.data || []
        const opts: SelectOption[] = (Array.isArray(raw) ? raw : []).map((r: any) => ({
          value: r.id,
          label: r.name || r.attributes?.name || `ID: ${r.id}`,
        }))
        setRoleOptions(prev => mergeOptions(prev, opts))
      } finally {
        setRoleLoading(false)
      }
    }
    load()
  }, [])

  // Each collection exposes a different label column — asking for a field a
  // content type doesn't have makes Strapi reject the whole query with 400.
  const searchEndpoint = useCallback(
    (
      endpoint: string,
      term: string,
      labelKey: string,
      onResults: (opts: SelectOption[]) => void,
      setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      clearTimeout(searchTimers.current[endpoint])
      searchTimers.current[endpoint] = setTimeout(async () => {
        setLoading(true)
        try {
          const params: any = {
            fields: [labelKey],
            publicationState: "preview",
            sort: "id:desc",
            pagination: { page: "1", pageSize: "25" },
          }
          if (term) {
            params.filters = { [labelKey]: { $containsi: term } }
          }
          const response = await HttpClient.get(endpoint, { params, skipDomainFilter: true })
          const opts: SelectOption[] = (response?.data || [])
            .filter((i: any) => i?.id)
            .map((i: any) => ({
              value: i.id,
              label: i.attributes?.[labelKey] || `ID: ${i.id}`,
            }))
          onResults(opts)
        } finally {
          setLoading(false)
        }
      }, 300)
    },
    [],
  )

  const mergeInto = useCallback(
    (setOpts: React.Dispatch<React.SetStateAction<SelectOption[]>>) =>
      (opts: SelectOption[]) =>
        setOpts(prev => mergeOptions(prev, opts)),
    [],
  )

  // Browsing options is a separate query from the assigned rows, so prime it
  // once on first focus rather than on every focus.
  const searchPrimedRef = useRef<Record<string, boolean>>({})

  const renderRelationField = (
    name: WebsiteRelation,
    label: string,
    endpoint: string,
    state: AssignedRelation,
    searching: boolean,
    setSearching: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    const busy = state.loading || searching

    return (
      <div>
        <Form.Item<WebsiteFieldType> name={name} label={label}>
          <Select
            mode="multiple"
            showSearch
            loading={busy}
            filterOption={false}
            // A website can link tens of thousands of rows; rendering one tag
            // each is what froze this form.
            maxTagCount="responsive"
            placeholder={t("Thêm quan hệ")}
            options={state.options}
            notFoundContent={busy ? <Spin size="small" /> : t("Không tìm thấy")}
            onSearch={term => searchEndpoint(endpoint, term, "name", state.addOptions, setSearching)}
            onFocus={() => {
              if (searchPrimedRef.current[name]) return
              searchPrimedRef.current[name] = true
              searchEndpoint(endpoint, "", "name", state.addOptions, setSearching)
            }}
          />
        </Form.Item>
        {state.total > 0 && (
          <div className="-mt-4 mb-4 flex items-center gap-2">
            <span className="text-xs text-[var(--admin-muted)]">
              {t("Đã hiển thị")} {state.loadedCount}/{state.total}
            </span>
            {state.loadedCount < state.total && (
              <Button size="small" type="link" loading={state.loadingMore} onClick={state.loadMore}>
                {t("Xem thêm")}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <Form
      form={form}
      name="website"
      initialValues={{ remember: true }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
      onValuesChange={onValuesChange}
      requiredMark="optional"
    >
      {/* ─── Cơ bản ─────────────────────────────────────────── */}
      <FormSection title={t("Cơ bản")} collapsible={false}>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item<WebsiteFieldType>
            name="domain"
            label={t("Tên miền")}
            rules={[{ required: true, message: t("Vui lòng chọn tên miền!") }]}
          >
            <Select placeholder={t("Chọn tên miền")} allowClear>
              {domainOptions.map((domain: { value: string; label: string }) => (
                <Select.Option key={domain.value} value={domain.value}>
                  {domain.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item<WebsiteFieldType>
            name="name"
            label={t("Tên website")}
            rules={[{ required: true, message: t("Vui lòng nhập tên website!") }]}
          >
            <Input placeholder={t("VD: Tamda Group")} />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item<WebsiteFieldType> name="letak" label="Letak">
            <Select
              showSearch
              allowClear
              loading={letakLoading}
              filterOption={false}
              placeholder={t("Thêm quan hệ")}
              options={letakOptions}
              notFoundContent={letakLoading ? <Spin size="small" /> : t("Không tìm thấy")}
              onSearch={term =>
                searchEndpoint(API_PREFIX_LETAKY_PATH, term, "title", mergeInto(setLetakOptions), setLetakLoading)
              }
              onFocus={() => {
                if (letakOptions.length === 0) {
                  searchEndpoint(API_PREFIX_LETAKY_PATH, "", "title", mergeInto(setLetakOptions), setLetakLoading)
                }
              }}
            />
          </Form.Item>

          <Form.Item<WebsiteFieldType> name="career" label="Career">
            <Select
              showSearch
              allowClear
              loading={careerLoading}
              filterOption={false}
              placeholder={t("Thêm quan hệ")}
              options={careerOptions}
              notFoundContent={careerLoading ? <Spin size="small" /> : t("Không tìm thấy")}
              onSearch={term =>
                searchEndpoint(API_PREFIX_CAREER_PATH, term, "position", mergeInto(setCareerOptions), setCareerLoading)
              }
              onFocus={() => {
                if (careerOptions.length === 0) {
                  searchEndpoint(API_PREFIX_CAREER_PATH, "", "position", mergeInto(setCareerOptions), setCareerLoading)
                }
              }}
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          {renderRelationField("posts", "Posts", API_PREFIX_POST_PATH, posts, postSearching, setPostSearching)}
          {renderRelationField("banners", "Banners", API_PREFIX_BANNER_PATH, banners, bannerSearching, setBannerSearching)}
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          {renderRelationField("events", "Events", API_PREFIX_EVENT_PATH, events, eventSearching, setEventSearching)}

          <Form.Item<WebsiteFieldType> name="roles" label="Roles">
            <Select
              mode="multiple"
              showSearch
              loading={roleLoading}
              maxTagCount="responsive"
              filterOption={(input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              placeholder={t("Thêm quan hệ")}
              options={roleOptions}
              notFoundContent={roleLoading ? <Spin size="small" /> : t("Không tìm thấy")}
            />
          </Form.Item>
        </div>
      </FormSection>
    </Form>
  )
}

export default WebsiteForm
